import { useMemo, useState } from 'react';
import type { FeedItem } from '@/mocks/gossip';
import { catMeta } from '@/mocks/gossip';
import ArticleModal from './ArticleModal';

interface Props {
  items: FeedItem[];
  onClose: () => void;
}

export default function SearchOverlay({ items, onClose }: Props) {
  const [kw, setKw] = useState('');
  const [active, setActive] = useState<FeedItem | null>(null);

  const results = useMemo(() => {
    const key = kw.trim().toLowerCase();
    if (!key) return [];
    return items.filter(
      (it) =>
        it.title.toLowerCase().includes(key) ||
        it.excerpt.toLowerCase().includes(key) ||
        catMeta[it.cat].toLowerCase().includes(key)
    );
  }, [kw, items]);

  return (
    <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-label="站内搜索">
      <button
        type="button"
        aria-label="关闭搜索"
        onClick={onClose}
        className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
      />
      <div className="relative z-10 mx-auto mt-[8vh] w-[92%] max-w-2xl">
        <div className="overflow-hidden rounded-xl bg-background-50 shadow-xl">
          <div className="flex items-center gap-3 border-b border-background-200 px-4 py-3 md:px-5">
            <i className="ri-search-line text-lg text-primary-500" />
            <input
              autoFocus
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              placeholder="搜八卦话题，如：红毯、综艺、盘点…"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="button"
              onClick={onClose}
              aria-label="关闭"
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-secondary-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
            >
              <i className="ri-close-line text-base" />
            </button>
          </div>
          <div className="max-h-[62vh] overflow-y-auto p-3 md:p-4">
            {kw.trim() === '' ? (
              <p className="px-2 py-6 text-center text-sm text-foreground-500">输入关键词，在全站图文里查找你关心的八卦话题。</p>
            ) : results.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-foreground-500">没有搜到相关内容，换个关键词试试，例如“红毯”“深扒”。</p>
            ) : (
              <ul className="space-y-2">
                {results.map((it) => (
                  <li key={it.id}>
                    <button
                      type="button"
                      onClick={() => setActive(it)}
                      className="flex w-full cursor-pointer items-start gap-3 rounded-lg bg-background-100 px-3 py-3 text-left transition hover:bg-primary-100/60"
                    >
                      <span className="mt-0.5 shrink-0 rounded bg-primary-100 px-2 py-0.5 text-[10px] font-medium text-primary-700">
                        {catMeta[it.cat]}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-foreground-900">{it.title}</span>
                        <span className="mt-1 block truncate text-xs text-foreground-500">{it.excerpt}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <ArticleModal item={active} onClose={() => setActive(null)} />
    </div>
  );
}